import Amplify from '@aws-amplify/core';

// Enable Amplify debugging for better insights when doing local dev.
(window as any).LOG_LEVEL = 'DEBUG';

/**
 * The client id query parameter
 */
const CLIENT_ID_QUERY_PARAMETER = 'client_id';

/**
 * The list of query parameters that should not be forwarded to the sign-in UI.
 */
const URL_OPENER_EXCLUDED_QUERY_PARAMETERS = [CLIENT_ID_QUERY_PARAMETER];

/**
 * The list of client ids that are allowed to be used in the Staging Website as overrides of the default one.
 * TODO: Move this configuration to GandalfClientConfiguration and consume it from there.
 */
const CLIENT_IDS_ALLOWLIST = [
  // GandalfHCProdUAT
  '1262iprkc8mo729eeo2omor0hf', // Dev IAD
  'knnmepn0b5a371peta4mg6013', // Gamma IAD
  '5u198f8d6ad8n49qhttm1e6q81', // Gamma DUB
  '2r8shl8i11u3120bsoeng46t2k', // Gamma PDX
  '45jm7pm2n7jedgi7m7cngkto44', // Gamma NRT
  '5tg2bb096s519eo09udh0pqoop', // Gamma SYD
  '1fpmboppod08s57tqmdi87r0pv', // Prod IAD
  '1ivbc2b1t8fcvhbljn905s3olm', // Prod DUB
  '1k7f7pibil9h2tvr28rudcgbuq', // Prod PDX
  '61rm5tq593p748lg0hnpb8r5b8', // Prod NRT
  '698kq5rv71mqv1mhaqvouh6fef', // Prod SYD

  // GandalfHCProdUATProfile
  '2ig7p49jsqop7v5efogbqkm1ag', // Dev IAD
  '73av83e14oq84ffidmo61mte2m', // Gamma IAD
  '10j6ap4f70ha7vpeuub28fcrv3', // Gamma DUB
  '2mei95c3oicgllf22ql3niah2p', // Gamma PDX
  '55p4rvrbom52ovkrkhh9n2a9lq', // Gamma NRT
  '62d42eb13jokne71bq2cajhupn', // Gamma SYD
  '75ac1lut5lc19pb9mlq0modlvg', // Prod IAD
  '6k5smto18nanctj4o3ad5lk4nk', // Prod DUB
  '6chrfahokhgvhsteiirjinomd1', // Prod PDX
  'tju5ucvt41mg6pksjck1livd9', // Prod NRT
  '38pj5340nqa9mem6s1scdrf16c', // Prod SYD

  // SkillBuilder Client IDs for Simplified Login Testing
  '3ecpsbmh50lsjqq74ql814dm26', // DEV IAD
  '2jfve6sri120cpj4rl1no9mok3', // Gamma NRT
  '472tlg79fkedkbqidjp1nkarn2', // Gamma SYD
  '3fvnoq6j161ts4gs68vli8j58t', // Gamma PDX
  '6reg4q49e6c6omkis0vlr4e26s', // Gamma DUB
  '1dgq152k7jope5oifdumv7b2p8', // Gamma IAD
  '7g601ca9ujfq0r3ivuu5tih1vc', // PROD NRT
  '6hvdu55jic4ploh9kp13i5vnij', // PROD SYD
  '1n96ml4cpvv0sje6dkj75p4am1', // PROD PDX
  '2ba4ibtra2ha3ucp0nuoei9ha2', // PROD DUB
  '4dn6pk2q9p4a743o38p96etl45', // PROD IAD

  // SimplifiedLoginEvents Client IDs for Staging Website Testing
  '2nn0kehvecgde7dan4937su4o7', // Beta IAD
  '5e6k4ma89bjln00vjkpoob5jft', // Gamma IAD
  '6oeosu0g1chtfl2mvmks7k6u1h', // Gamma PDX
  '40fl0e6mfjatghh1jo41ekq68m', // Gamma DUB
  'ruthiqm85obslf05ivbn6a3ki', // Gamma NRT
  '3571vp4mhstbihv86b8dtj9h3r', // Gamma SYD
  '275puefv2i6i6f1dmhd6bk7oug', // Prod IAD
  '191b57tlo87tm5v8dmkuvu0hd0', // Prod PDX
  '1fc89646otkmob4ck3bth20i22', // Prod DUB
  'm18g2a8m4kjv0cmuievpjn2ud', // Prod NRT
  '2j2bo12feqgra159i9vt2nqt4d', // Prod SYD
];

const addUrlParameters = (url: string): string => {
  const urlObj = new URL(url);
  const params = new URLSearchParams(window.location.search);
  params.forEach((value, key) => {
    if (!URL_OPENER_EXCLUDED_QUERY_PARAMETERS.includes(key)) {
      urlObj.searchParams.append(key, value);
    }
  });
  return urlObj.toString();
};

const generateNewUrl = (
  domain: string,
  signInUIDomain: string,
  url: string
): string => {
  const loginRequestUrl = `https://${domain}/oauth2/authorize`;
  let newUrl = url;
  if (signInUIDomain && url.startsWith(loginRequestUrl)) {
    newUrl = url.replace(loginRequestUrl, `https://${signInUIDomain}/login`);
  }
  return addUrlParameters(newUrl);
};

// Creates a urlOpener function to intercept sign in calls and change the url.
// See docs, https://aws-amplify.github.io/amplify-js/api/interfaces/awscognitooauthopts.html
const createUrlInterceptor =
  (domain: string, signInUIDomain: string) => (url: string) => {
    const newUrl = generateNewUrl(domain, signInUIDomain, url);
    const windowProxy = window.open(newUrl, '_self');
    return windowProxy ? Promise.resolve(windowProxy) : Promise.reject();
  };

const getAmplifyConfig = (config: any, useCustomSignInUI: boolean): any => {
  const defaultConfig = {
    Auth: {
      region: config.Auth.region,
      userPoolId: config.Auth.userPoolId,
      userPoolWebClientId: config.Auth.userPoolWebClientId,
      oauth: {
        domain: config.Auth.oauth.domain,
        scope: config.Auth.oauth.scope,
        responseType: config.Auth.oauth.responseType,
        redirectSignIn: config.Auth.oauth.redirectSignIn,
        redirectSignOut: config.Auth.oauth.redirectSignOut,
        // (optional) This is used to intercept login requests to navigate to the Custom SignIn UI.
        ...(useCustomSignInUI && {
          urlOpener: createUrlInterceptor(
            config.Auth.oauth.domain,
            config.Gandalf.customSignInDomain
          ),
        }),
      },
    },
  };
  const params = new URLSearchParams(window.location.search);
  const clientIdOverride = params.get(CLIENT_ID_QUERY_PARAMETER);
  if (!clientIdOverride || !CLIENT_IDS_ALLOWLIST.includes(clientIdOverride)) {
    return defaultConfig;
  }
  const redirectSignInUrlObj = new URL(defaultConfig.Auth.oauth.redirectSignIn);
  redirectSignInUrlObj.searchParams.append(
    CLIENT_ID_QUERY_PARAMETER,
    clientIdOverride
  );
  return {
    ...defaultConfig,
    Auth: {
      ...defaultConfig.Auth,
      userPoolWebClientId: clientIdOverride,
      oauth: {
        ...defaultConfig.Auth.oauth,
        redirectSignIn: redirectSignInUrlObj.toString(),
      },
    },
  };
};

const configureAmplify = ({
  config,
  useCustomSignInUI,
}: {
  config: any;
  useCustomSignInUI: boolean;
}) => {
  return Amplify.configure(getAmplifyConfig(config, useCustomSignInUI));
};

export default configureAmplify;
